<script setup lang="ts">
  import Breadcrumb from '@/components/Breadcrumb.vue'
  import LegacySideBar from '@/components/legacy/LegacySideBar.vue'
  import { I18NEXT } from '@/i18n'
  import { loadLegacy } from '@/legacy/legacy-main'
  import { menuItems } from '@/legacy/legacy-routes'
  import { useLegacyAppStore } from '@/store/legacy-app.store'
  import { useUserStore } from '@/store/user.store'
  import { require, ToastMessage } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, type Ref, ref, toRefs, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const disableLangModules = [107]

  const route = useRoute()
  const router = useRouter()

  const appStore = useLegacyAppStore()
  const { appInitData, subModuleIds } = toRefs(appStore)

  const userStore = useUserStore()

  const actualMenu = ref(menuItems)

  const sidebarToggled = ref(false)

  function calculateMenu(subModuleIds: number[]) {
    const filteredMenu = menuItems.filter(
      module => module.subModuleId == null || subModuleIds.includes(module.subModuleId)
    )
    for (const menuItem of filteredMenu) {
      menuItem.subItems = menuItem.subItems?.filter(
        module => module.subModuleId == null || subModuleIds.includes(module.subModuleId)
      )
    }

    return filteredMenu.filter(
      menuItem => (menuItem.subItems?.length ?? 0) > 0 || menuItem.url != null
    )
  }

  watch(
    [() => appInitData.value, () => userStore.currentNestUser],
    () => {
      if (appInitData.value == null || userStore.currentNestUser == null) {
        return
      }

      actualMenu.value = calculateMenu(subModuleIds.value)
      const firstMenuItem = require(actualMenu.value[0], 'No menu items are available.')
      if (route.fullPath === '/' || route.fullPath === '/home') {
        const url = firstMenuItem.url ?? firstMenuItem.subItems?.[0]?.url
        router.push(require(url))
      }
    },
    { immediate: true }
  )

  const firstName: Ref<string> = ref('')
  const lastName: Ref<string> = ref('')
  const organizationName: Ref<string> = ref('')

  const showLanguage = computed(
    () => !disableLangModules.some(module => subModuleIds.value.includes(module))
  )

  onMounted(async () => {
    loadLegacy()
    const response = require(await appStore.fetchAppInitData())
    await userStore.fetchCurrentUser()
    await userStore.fetchMe()
    firstName.value = response.user.firstName
    lastName.value = response.user.lastName
    organizationName.value = response.user.userOrganisatie.organisatieNaam
    window.i18Editor = new I18NEXT(response)
    window.i18Editor.initTranslations(response)
  })
</script>

<template>
  <!-- Error Modal -->
  <div
    id="global-error-modal"
    class="modal fade"
    tabindex="-1"
    style="z-index: 9999 !important"
    role="dialog"
    aria-labelledby="global-error-modal-label"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-sm modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <div class="row">
            <div class="col-12">
              <div class="d-flex justify-content-center mb-3">
                <i class="fa-solid fa-4x fa-triangle-exclamation"></i>
              </div>
              <div>
                <p id="global-error-modal-message" class="text-center h3"></p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">
            Sluiten
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Prompt? Modal -->
  <div
    id="global-prompt-modal"
    class="modal fade"
    tabindex="-1"
    style="z-index: 9999 !important"
    role="dialog"
    aria-labelledby="global-prompt-modal-label"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-sm modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="global-prompt-modal-title" class="modal-title">Title</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <div class="col-12">
              <div class="d-flex justify-content-center mb-3">
                <i class="fa-solid fa-4x fa-triangle-exclamation"></i>
              </div>
              <div>
                <p id="global-prompt-modal-message" class="text-center h3"></p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            id="global-prompt-modal-cancel"
            type="button"
            class="btn btn-outline-primary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            id="global-prompt-modal-ok"
            type="button"
            class="btn btn-primary"
            data-bs-dismiss="modal"
          >
            Ok
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="wrapper">
    <LegacySideBar
      :toggled="sidebarToggled"
      :firstName="firstName"
      :lastName="lastName"
      :organizationName="organizationName"
      :menu="actualMenu"
    />
    <!-- Toasts -->
    <div class="position-fixed bottom-0 end-0 p-3" style="z-index: 9999">
      <ToastMessage />
      <div
        id="toast"
        class="toast align-items-center text-white bg-primary border-0"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="d-flex">
          <div id="toast-body" class="toast-body">Actie was succesvol.</div>
          <button
            type="button"
            class="btn-close btn-close-white me-2 m-auto"
            data-bs-dismiss="toast"
            aria-label="Close"
          ></button>
        </div>
      </div>
      <div
        id="error-toast"
        class="toast align-items-center text-white bg-danger border-0"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="d-flex">
          <div id="error-toast-body" class="toast-body">
            Actie is gefaald, probeer het later opnieuw of neem contact op met de helpdesk.
          </div>
          <button
            type="button"
            class="btn-close btn-close-white me-2 m-auto"
            data-bs-dismiss="toast"
            aria-label="Close"
          ></button>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div
      id="global-error-modal"
      class="modal fade"
      tabindex="-1"
      style="z-index: 9999 !important"
      role="dialog"
      aria-labelledby="global-error-modal-label"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-sm modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <div class="row">
              <div class="col-12">
                <div class="d-flex justify-content-center mb-3">
                  <i class="fa-solid fa-4x fa-triangle-exclamation"></i>
                </div>
                <div>
                  <p id="global-error-modal-message" class="text-center h3"></p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Prompt? Modal -->
    <div
      id="global-prompt-modal"
      class="modal fade"
      tabindex="-1"
      style="z-index: 9999 !important"
      role="dialog"
      aria-labelledby="global-prompt-modal-label"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-sm modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="global-prompt-modal-title" class="modal-title">Title</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-12">
                <div class="d-flex justify-content-center mb-3">
                  <i class="fa-solid fa-4x fa-triangle-exclamation"></i>
                </div>
                <div>
                  <p id="global-prompt-modal-message" class="text-center h3"></p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              id="global-prompt-modal-cancel"
              type="button"
              class="btn btn-outline-primary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              id="global-prompt-modal-ok"
              type="button"
              class="btn btn-primary"
              data-bs-dismiss="modal"
            >
              Ok
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="main scrollGradient d-flex flex-column" style="height: 100vh">
      <nav class="navbar-blur">
        <div class="navbar navbar-expand navbar-theme navbar-lzp">
          <a
            id="navbar-hamburger"
            class="sidebar-toggle d-flex me-2"
            @click="sidebarToggled = !sidebarToggled"
          >
            <i class="hamburger align-self-center"></i>
          </a>

          <div class="header-container">
            <Breadcrumb />
          </div>

          <div class="navbar-collapse collapse">
            <ul id="main-navbar" class="navbar-nav ms-auto">
              <li v-if="showLanguage" class="nav-item dropdown ms-lg-2">
                <a
                  id="langDropdown"
                  class="nav-link dropdown-toggle position-relative"
                  href="#"
                  data-bs-toggle="dropdown"
                >
                  <i id="selected-flag" class="align-middle fi fi-gb"></i>
                </a>
                <div class="dropdown-menu dropdown-menu-end" aria-labelledby="langDropdown">
                  <a
                    class="dropdown-item"
                    role="button"
                    onclick="i18Editor.changeLanguage('NL',' fi-nl')"
                    ><i class="align-middle me-1 fi fi-nl"></i> Nederlands</a
                  >
                  <a
                    class="dropdown-item"
                    role="button"
                    onclick="i18Editor.changeLanguage('EN','fi-gb')"
                    ><i class="align-middle me-1 fi fi-gb"></i> English</a
                  >
                  <a
                    class="dropdown-item"
                    role="button"
                    onclick="i18Editor.changeLanguage('FR',' fi-fr')"
                    ><i class="align-middle me-1 fi fi-fr"></i> French</a
                  >
                </div>
              </li>
              <li class="nav-item dropdown ms-lg-2">
                <a
                  id="userDropdown"
                  class="nav-link dropdown-toggle position-relative"
                  href="#"
                  data-bs-toggle="dropdown"
                >
                  <i class="align-middle fas fa-cog"></i>
                </a>
                <div class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                  <a class="dropdown-item" role="button" onclick="logOut()"
                    ><i class="align-middle me-1 fas fa-fw fa-arrow-alt-circle-right"></i> Sign
                    out</a
                  >
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      <RouterView />

      <template id="loading-content-template">
        <div
          class="h-100 w-100 d-flex justify-content-center align-items-center"
          style="left: 0; top: 0"
        >
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </template>

      <footer style="display: none" class="footer shadow-lg">
        <div class="container-fluid">
          <div class="row text-muted">
            <div class="col-8 text-start"></div>
            <div class="col-4 text-end">
              <p class="mb-0">
                &copy;[[${#dates.year(#dates.createNow())}]] -
                <a href="https://www.lzp.nl/" class="text-muted">LZP</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<style scoped>
  .header-title {
    margin-top: 0.75rem;
    font-size: 1.33rem;
    margin-left: 0;
    padding-left: 1rem;
  }

  .header-container {
    display: flex;
    flex-direction: row;
  }
</style>
